import {
  _convertPropertyValuesToArray,
  AbstractEntity,
  logComputeFunction,
  ParamsByNames,
  PlethoraLib,
  PropertyValuesDictionary,
  Transaction,
  UiData,
} from "plethora-lib"
import { AbstractUiEntity } from "../UiClasses/AbstractUiEntity"
import { DataTypeShapeType, DataTypeDirection, DataTypeSize, DataTypeColor } from "./GeneratedDataTypes"

export interface IHelperPropertiesValues {
  Direction: DataTypeDirection
  X: number
  Y: number
}

export abstract class GeneratedHelper extends AbstractUiEntity {
  constructor(id: number, propertyValues: PropertyValuesDictionary, uiData: UiData) {
    super("Helper", id, propertyValues, ["Player", "Clickable", "Locatable"], uiData)
  }

  public computeFunction(propertyName: string, functionParameters: ParamsByNames, uiData: UiData): number {
    let result: number
    switch (propertyName) {
      default:
        throw new Error("Error on computeFunction(), unknown computed property name " + propertyName)
    }
    if (typeof result !== "number") {
      throw new Error("Error on computeFunction(), result is not a number")
    }
    logComputeFunction(this.id, this.name, propertyName, functionParameters, uiData, result)
    return result
  }

  protected onPropertyUpdate(propertyName: string, newValue: any, oldValue: any, uiData: UiData): void {
    super.onPropertyUpdate(propertyName, newValue, oldValue, uiData)
    switch (propertyName) {
      case "Direction": {
        this.onDirectionUpdate(newValue, oldValue, uiData)
        return
      }

      case "X": {
        this.onXUpdate(newValue, oldValue, uiData)
        return
      }

      case "Y": {
        this.onYUpdate(newValue, oldValue, uiData)
        return
      }

      default:
        throw new Error("Unkown property " + propertyName)
    }
  }

  public getDirection(): DataTypeDirection {
    return this.getPropertyValue("Direction") as DataTypeDirection
  }

  public setDirection(value: DataTypeDirection, uiData: UiData = null): void {
    this.setPropertyValue("Direction", value, uiData, true, null)
  }
  public transactionSetDirection(transaction: Transaction, value: DataTypeDirection, uiData: UiData = null): void {
    this.setPropertyValue("Direction", value, uiData, true, transaction)
  }

  protected abstract onDirectionUpdate(newValue: DataTypeDirection, oldValue: DataTypeDirection, uiData: UiData): void

  public getX(): number {
    return this.getPropertyValue("X") as number
  }

  public setX(value: number, uiData: UiData = null): void {
    this.setPropertyValue("X", value, uiData, true, null)
  }
  public transactionSetX(transaction: Transaction, value: number, uiData: UiData = null): void {
    this.setPropertyValue("X", value, uiData, true, transaction)
  }

  protected abstract onXUpdate(newValue: number, oldValue: number, uiData: UiData): void

  public getY(): number {
    return this.getPropertyValue("Y") as number
  }

  public setY(value: number, uiData: UiData = null): void {
    this.setPropertyValue("Y", value, uiData, true, null)
  }
  public transactionSetY(transaction: Transaction, value: number, uiData: UiData = null): void {
    this.setPropertyValue("Y", value, uiData, true, transaction)
  }

  protected abstract onYUpdate(newValue: number, oldValue: number, uiData: UiData): void

  protected onAction(actionName: string, actionParameters: ParamsByNames, uiData: UiData): void {
    super.onAction(actionName, actionParameters, uiData)
    switch (actionName) {
      case "Turn Down": {
        this.onTurnDown(uiData)
        return
      }

      case "Turn Up": {
        this.onTurnUp(uiData)
        return
      }

      case "Turn Right": {
        this.onTurnRight(uiData)
        return
      }

      case "Turn Left": {
        this.onTurnLeft(uiData)
        return
      }

      case "Step1": {
        this.onStep1(uiData)
        return
      }

      case "Step3": {
        this.onStep3(uiData)
        return
      }

      case "Step2": {
        this.onStep2(uiData)
        return
      }

      case "Click": {
        this.onClick(uiData)
        return
      }

      default:
        throw new Error("Unkown action " + actionName)
    }
  }

  public applyTurnDown(uiData: UiData = null): void {
    this.applyAction("Turn Down", {}, uiData, true, null)
  }
  public transactionApplyTurnDown(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Turn Down", {}, uiData, true, transaction)
  }
  protected abstract onTurnDown(uiData: UiData): void

  public applyTurnUp(uiData: UiData = null): void {
    this.applyAction("Turn Up", {}, uiData, true, null)
  }
  public transactionApplyTurnUp(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Turn Up", {}, uiData, true, transaction)
  }
  protected abstract onTurnUp(uiData: UiData): void

  public applyTurnRight(uiData: UiData = null): void {
    this.applyAction("Turn Right", {}, uiData, true, null)
  }
  public transactionApplyTurnRight(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Turn Right", {}, uiData, true, transaction)
  }
  protected abstract onTurnRight(uiData: UiData): void

  public applyTurnLeft(uiData: UiData = null): void {
    this.applyAction("Turn Left", {}, uiData, true, null)
  }
  public transactionApplyTurnLeft(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Turn Left", {}, uiData, true, transaction)
  }
  protected abstract onTurnLeft(uiData: UiData): void

  public applyStep1(uiData: UiData = null): void {
    this.applyAction("Step1", {}, uiData, true, null)
  }
  public transactionApplyStep1(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Step1", {}, uiData, true, transaction)
  }
  protected abstract onStep1(uiData: UiData): void

  public applyStep3(uiData: UiData = null): void {
    this.applyAction("Step3", {}, uiData, true, null)
  }
  public transactionApplyStep3(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Step3", {}, uiData, true, transaction)
  }
  protected abstract onStep3(uiData: UiData): void

  public applyStep2(uiData: UiData = null): void {
    this.applyAction("Step2", {}, uiData, true, null)
  }
  public transactionApplyStep2(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Step2", {}, uiData, true, transaction)
  }
  protected abstract onStep2(uiData: UiData): void

  public applyClick(uiData: UiData = null): void {
    this.applyAction("Click", {}, uiData, true, null)
  }
  public transactionApplyClick(transaction: Transaction, uiData: UiData = null): void {
    this.applyAction("Click", {}, uiData, true, transaction)
  }
  protected abstract onClick(uiData: UiData): void

  protected onInteraction(
    interactionName: string,
    reactorEntity: AbstractEntity,
    interactionParameters: ParamsByNames,
    uiData: UiData
  ): void {
    super.onInteraction(interactionName, reactorEntity, interactionParameters, uiData)
    switch (interactionName) {
      case "Meets": {
        this.onMeets(true, reactorEntity, uiData) // activator
        ;(reactorEntity as any).onMeets(false, this, uiData) // reactor
        return
      }

      default:
        throw new Error("Unkown interaction " + interactionName)
    }
  }

  public applyMeets(reactorId: number, uiData: UiData = null): void {
    this.applyInteraction(reactorId, "Meets", {}, uiData, true, null)
  }
  public transactionApplyMeets(transaction: Transaction, reactorId: number, uiData: UiData = null): void {
    this.applyInteraction(reactorId, "Meets", {}, uiData, true, transaction)
  }
  protected abstract onMeets(isActivator: boolean, otherEntity: AbstractEntity, uiData: UiData): void

  public static createRequest(
    propertyValues: Partial<IHelperPropertiesValues>,
    quantity: number,
    uiData: UiData = null
  ): void {
    PlethoraLib.getInstance()
      .getMessagesManager()
      .sendCreateRequestMessage(
        {
          entityName: "Helper",
          properties: _convertPropertyValuesToArray(propertyValues),
          quantity,
        },
        uiData
      )
  }
}
